import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  createTaskApprovalGroup,
  destroyTaskApprovalGroup,
  fetchTaskApprovalGroup,
  updateTaskApprovalGroup,
} from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import { sortBy, uniqueId } from 'lodash'
import ApproverSettingModal from '../../../components/task/ApproverSettingModal'
import ConfirmationModal from '../../../components/task/ConfirmationModal'
import ApproverSettingForMemberModal from '../../../components/task/ApproverSettingForMemberModal'
import VideoGuide from '../../../components/common/VideoGuide'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { RequestPatchTaskApprovalGroup, RequestPostTaskApprovalGroup } from '../../../../states/types/task'

interface MemberDetail {
  id: number
  name: string
  kana: string | null
  profile_image: string | null
}

interface ApproverMemberDetail extends MemberDetail {
  priority: number
}

interface ApprovalGroup {
  id: number
  name: string
  description: string | null
  created_by: string | null
  approve_members: ApproverMemberDetail[]
  request_members: MemberDetail[]
  viewable_members: MemberDetail[]
}

interface ApproverSettingModalState {
  display: boolean
  type: string
  id: number | undefined
  targetData: ApprovalGroup | null
  memberList: MemberDetail[]
  teamList: {
    code: string | null
    id: number
    name: string
    parent_team_id: number | null
    managers: MemberDetail[]
    members: MemberDetail[]
  }[]
  alreadyAssignedMemberIds: number[]
}

interface ApproverSettingForMemberModalState {
  display: boolean
  targetMember: MemberDetail | null
  targetData: ApprovalGroup | null
  approvalGroupList: ApprovalGroup[]
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: VoidFunction,
}

const Index: FC = () => {
  const {
    login,
    task,
  } = useSelector((state: RootState) => state)

  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const [data, setData] = useState<ApprovalGroup[]>([])

  const [isDisplayGroupList, setIsDisplayGroupList] = useState<boolean>(true)

  const [approverSettingModalState, setApproverSettingModalState] = useState<ApproverSettingModalState>({
    display: false,
    type: "",
    id: undefined,
    targetData: null,
    memberList: [],
    teamList: [],
    alreadyAssignedMemberIds: [],
  })

  const [approverSettingForMemberModalState, setApproverSettingForMemberModalState] = useState<ApproverSettingForMemberModalState>({
    display: false,
    targetMember: null,
    targetData: null,
    approvalGroupList: [],
  })

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const buildApprovalFlow = (list: ApproverMemberDetail[]) => {
    if (!list.length) return (<>承認フローが設定されていません！</>)
    return (
      <>
        {
          list.map((member, i) => (
            <React.Fragment key={uniqueId()}>
              <FontAwesomeIcon icon={['fas', 'arrow-right']} />
              <div className="approver_block">
                <div className="approver_block_title">
                  {list.length === i + 1 ? "最終" : `${i + 1}次`}
                  承認者
                </div>
                <div className="approver_block_name">
                  <img
                    alt="profile"
                    src={member.profile_image || `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                    className="profile_icon"
                  />
                  <ruby>
                    {member.name}
                    <rt>{member.kana}</rt>
                  </ruby>
                </div>
              </div>
            </React.Fragment>
          ))
        }
      </>
    )
  }

  const buildMemberList = (list: MemberDetail[], type: string) => {
    if (!list.length) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            padding: "8px",
            minHeight: "50px",
          }}
        >
          {`${type}ユーザーが設定されていません！`}
        </div>
      )
    }
    return (
      <>
        <div className="member_list">
          {
            list.map((member) => (
              <React.Fragment key={uniqueId()}>
                <div className="member_label">
                  <img
                    alt="profile"
                    src={member.profile_image || `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                    className="profile_icon"
                  />
                  <ruby>
                    {member.name}
                    <rt>{member.kana}</rt>
                  </ruby>
                </div>
              </React.Fragment>
            ))
          }
        </div>
      </>
    )
  }

  const findApproverData = (memberId: number) => (
    data.find((v) => v.request_members.map((w) => w.id).includes(memberId))
  )

  const buildApprovalFlowForPersonal = (memberId: number) => {
    const targetData = findApproverData(memberId)
    if (!targetData) {
      return (
        <div
          style={{
            textAlign: "center",
          }}
        >
          承認フローが設定されていません！
        </div>
      )
    }
    return (
      <>
        <div>
          現在の設定:
          <span
            style={{
              fontWeight: "bold",
              marginLeft: "4px",
            }}
          >
            {targetData.name}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: targetData.approve_members.length ? "left" : "center",
            gap: "4px",
            padding: "8px",
          }}
        >
          {buildApprovalFlow(sortBy(targetData.approve_members, "priority").reverse())}
        </div>
      </>
    )
  }

  const closeApprovalModal = () => {
    setApproverSettingModalState({
      ...approverSettingModalState,
      targetData: null,
      display: false,
    })
  }

  const createApprovalGroup = (params: RequestPostTaskApprovalGroup, duplicationIds: number[]) => {
    dispatch(
      createTaskApprovalGroup(
        params,
        duplicationIds,
        () => {
          alert.success(`承認グループ「${params.name}」を作成しました`)
        },
      ),
    )
    closeApprovalModal()
  }

  const updateApprovalGroup = (params: RequestPatchTaskApprovalGroup, duplicationIds: number[]) => {
    const tmpName = params.name || data.find((v) => v.id === params.id)?.name
    dispatch(
      updateTaskApprovalGroup(
        params,
        duplicationIds,
        () => {
          if (tmpName) {
            alert.success(`承認グループ「${tmpName}」を変更しました`)
          } else {
            alert.success(`変更しました`)
          }
        },
      ),
    )
    closeApprovalModal()
  }

  const deleteApprovalGroup = (groupId: number, groupName: string) => {
    dispatch(
      destroyTaskApprovalGroup(
        { id: groupId.toString() },
        () => {
          alert.success(`承認グループ「${groupName}」を削除しました`)
        },
      ),
    )
    closeApprovalModal()
  }

  const closeApprovalForMemberModal = () => {
    setApproverSettingForMemberModalState({
      ...approverSettingForMemberModalState,
      targetData: null,
      targetMember: null,
      display: false,
    })
  }

  const updateApprovalGroupForMember = (approvalGroupId: number | null, memberId: number) => {
    const targetParam = task.taskApprovalGroup?.approval_groups.find((v) => v.id === approvalGroupId)
    if (!targetParam) {
      const ejectGroup = task.taskApprovalGroup?.approval_groups.find((v) => v.request_members.find((w) => w.id === memberId))
      if (!ejectGroup) {
        alert.error("承認グループの所属に関するエラーが発生しました。ページをリロードしてもう一度実行してください。")
        return
      }
      dispatch(
        updateTaskApprovalGroup(
          {
            id: ejectGroup.id,
            request_member_ids: ejectGroup.request_members.filter((v) => v.id !== memberId).map((v) => v.id),
          },
          [],
          () => {
            alert.success(`承認グループを未設定状態に変更しました`)
          },
        ),
      )
    } else {
      dispatch(
        updateTaskApprovalGroup(
          {
            id: targetParam.id,
            request_member_ids: [...targetParam.request_members.map((v) => v.id), memberId],
          },
          [memberId],
          () => {
            alert.success(`承認グループ「${targetParam.name}」に変更しました`)
          },
        ),
      )
    }
    closeApprovalForMemberModal()
  }

  // confirmation modal
  const openConfirmationModal = (groupId: number, type: string) => {
    const approvalGroupData = task.taskApprovalGroup?.approval_groups.find((v) => v.id === groupId)
    if (!approvalGroupData) return
    let callback = () => {
      // none
    }
    switch (type) {
      case "delete":
        callback = () => deleteApprovalGroup(groupId, approvalGroupData.name)
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: approvalGroupData.name,
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const openApprovalModal = (id: number, type: string) => {
    if (type === "create") {
      setApproverSettingModalState({
        ...approverSettingModalState,
        display: true,
        type: type,
        id: 0,
        targetData: null,
        memberList: task.taskApprovalGroup?.members || [],
        teamList: task.taskApprovalGroup?.teams || [],
        alreadyAssignedMemberIds:
          task.taskApprovalGroup
            ? task.taskApprovalGroup.approval_groups.map((v) => v.request_members.map((w) => w.id)).flat()
            : [],
      })
    } else {
      const approvalGroupData = task.taskApprovalGroup?.approval_groups.find((v) => v.id === id)
      setApproverSettingModalState({
        ...approverSettingModalState,
        display: true,
        type: type,
        id: id,
        targetData: approvalGroupData || null,
        memberList: task.taskApprovalGroup?.members || [],
        teamList: task.taskApprovalGroup?.teams || [],
        alreadyAssignedMemberIds:
          task.taskApprovalGroup
            ? task.taskApprovalGroup?.approval_groups.filter((v) => v.id !== id)
              .map((v) => v.request_members.map((w) => w.id)).flat()
            : [],
      })
    }
  }

  const openApprovalForMemberModal = (approval_group_id: number | null, member_id: number) => {
    const approvalGroupData = task.taskApprovalGroup?.approval_groups.find((v) => v.id === approval_group_id)
    const memberData = task.taskApprovalGroup?.members.find((v) => v.id === member_id)
    setApproverSettingForMemberModalState({
      ...approverSettingForMemberModalState,
      display: true,
      targetMember: memberData || null,
      targetData: approvalGroupData || null,
      approvalGroupList: task.taskApprovalGroup?.approval_groups || [],
    })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(fetchTaskApprovalGroup())
  }, [login.accessToken])

  useEffect(() => {
    setData(task.taskApprovalGroup?.approval_groups || [])
  }, [task])

  return (
    <section className="task_page setting_page">
      <Helmet>
        <title>
          {
            location.pathname === `/${language}${URL.SETTING_TASK_APPROVAL_SETTING.path}` ? (
              `Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 承認グループ設定`
            ) : (
              `Qasee - 工数管理 - 承認グループ設定`
            )
          }
        </title>
      </Helmet>
      <section className="breadcrumb">
        {
          location.pathname === `/${language}${URL.SETTING_TASK_APPROVAL_SETTING.path}` ? (
            <>
              設定
              &nbsp;&gt;&nbsp;
              <Link
                to={setPath(URL.SETTING_COMMON.path, language)}
              >
                {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
              </Link>
            </>
          ) : (
            <>
              工数管理
            </>
          )
        }
        &nbsp;&gt;&nbsp;
        承認グループ設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          承認グループ設定
          <VideoGuide
            videoId="Zq5eMP_IdcA"
          />
        </h1>
        <div className="tools">
          <div className="button_area">
            {
              isDisplayGroupList && (
                <button
                  type="button"
                  className="button"
                  onClick={(event) => openApprovalModal(Number(event.currentTarget.dataset.id), "create")}
                >
                  <FontAwesomeIcon icon={['fas', 'plus']} />
                  &nbsp;
                  承認グループを作成する
                </button>
              )
            }
          </div>
        </div>
      </section>
      <section className="page_description">
        <p>
          承認グループを作成して各ユーザーの承認フローを登録・変更できます。
        </p>
        <p>
          申請承認機能を使用することで、Qasee上で業務仕分けした時間や勤怠時間を許可がなければ修正できないようデータにロックをかけることが可能になります。
        </p>
      </section>
      {
        task.isLoading ? (
          <section className="page_contents content-area" key={uniqueId()}>
            <div className="content-block">
              <div className="block-detail">
                <LoadingSquare>
                  <>
                    承認設定を読み込んでいます…
                  </>
                </LoadingSquare>
              </div>
            </div>
          </section>
        ) : (
          <>
            <section className="page_contents content-area" key={uniqueId()}>
              <div className="content-block">
                <div className="block-detail">
                  <div className="block-header">
                    <div className="changeable_table_header">
                      <div className="changeable_table_header_title">
                        {isDisplayGroupList ? "承認グループ別設定" : "ユーザー別設定"}
                      </div>
                      <div className="button_area">
                        {
                          isDisplayGroupList ? (
                            <>
                              <Tips
                                description="ユーザー表示に切り替えます"
                                className=""
                                icon={false}
                              >
                                <>
                                  <button
                                    type="button"
                                    onClick={() => setIsDisplayGroupList(false)}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "2px",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['far', 'object-group']} />
                                      グループ表示
                                    </div>
                                    <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "2px",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'users']} />
                                      ユーザー表示
                                    </div>
                                  </button>
                                </>
                              </Tips>
                            </>
                          ) : (
                            <>
                              <Tips
                                description="グループ表示に切り替えます"
                                className=""
                                icon={false}
                              >
                                <>
                                  <button
                                    type="button"
                                    onClick={() => setIsDisplayGroupList(true)}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "2px",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'users']} />
                                      ユーザー表示
                                    </div>
                                    <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "2px",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['far', 'object-group']} />
                                      グループ表示
                                    </div>
                                  </button>
                                </>
                              </Tips>
                            </>
                          )
                        }
                      </div>
                    </div>
                  </div>
                  {
                    isDisplayGroupList ? (
                      <>
                        <table
                          id="hover-table"
                          className="flex-table team_list_table"
                        >
                          <thead>
                            <tr>
                              <th>
                                操作
                              </th>
                              <th>
                                承認グループ名
                              </th>
                              <th>
                                承認フロー・申請ユーザー・閲覧ユーザー
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {
                              data.map((v) => (
                                <tr key={uniqueId()}>
                                  <td>
                                    <div>
                                      <div>
                                        <Tips
                                          description="承認グループを編集"
                                          className=""
                                          icon={false}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => openApprovalModal(v.id, "update")}
                                          >
                                            <FontAwesomeIcon icon={['fas', 'tools']} />
                                          </button>
                                        </Tips>
                                      </div>
                                      <div>
                                        <Tips
                                          description="承認グループを削除"
                                          className=""
                                          icon={false}
                                        >
                                          <button
                                            type="button"
                                            className="del"
                                            onClick={() => openConfirmationModal(v.id, "delete")}
                                          >
                                            <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                          </button>
                                        </Tips>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="no_trans">
                                    {v.name}
                                  </td>
                                  <td className="no_trans">
                                    <div className="approval_setting_box">
                                      <div className="approver_area">
                                        {
                                          !!v.approve_members.length && (
                                            <div className="approver_block member">
                                              申請ユーザー
                                            </div>
                                          )
                                        }
                                        {buildApprovalFlow(sortBy(v.approve_members, "priority").reverse())}
                                      </div>
                                      <div
                                        style={{
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "4px",
                                          padding: "4px",
                                          margin: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            color: "#999999",
                                          }}
                                        >
                                          申請ユーザー
                                        </div>
                                        {buildMemberList(v.request_members || [], "申請")}
                                      </div>
                                      <div
                                        style={{
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "4px",
                                          padding: "4px",
                                          margin: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            color: "#999999",
                                          }}
                                        >
                                          閲覧ユーザー
                                        </div>
                                        {buildMemberList(v.viewable_members || [], "閲覧")}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                        {
                          !!data.length && (
                            <button
                              type="button"
                              className="create_group"
                              onClick={(event) => openApprovalModal(Number(event.currentTarget.dataset.id), "create")}
                            >
                              <FontAwesomeIcon icon={['fas', 'plus']} />
                              &nbsp;
                              承認グループを作成する
                            </button>
                          )
                        }
                        {
                          !data.length && (
                            <div
                              style={{
                                textAlign: "center",
                                marginTop: "16px",
                              }}
                            >
                              <div
                                style={{
                                  padding: "16px",
                                  fontSize: "20px",
                                  color: "#999999",
                                }}
                              >
                                承認グループがありません
                              </div>
                              <button
                                type="button"
                                className="button add_team_button"
                                onClick={(event) => openApprovalModal(Number(event.currentTarget.dataset.id), "create")}
                              >
                                承認グループを作成しましょう
                              </button>
                            </div>
                          )
                        }
                      </>
                    ) : (
                      <>
                        <table
                          id="hover-table"
                          className="flex-table team_list_table"
                        >
                          <thead>
                            <tr>
                              <th>
                                操作
                              </th>
                              <th>
                                ユーザー名
                              </th>
                              <th>
                                承認フロー
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {
                              task.taskApprovalGroup?.members.map((v) => (
                                <tr key={uniqueId()}>
                                  <td>
                                    <div>
                                      <div>
                                        <Tips
                                          description="所属する承認グループを変更"
                                          className=""
                                          icon={false}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => openApprovalForMemberModal(
                                              findApproverData(v.id)?.id || null,
                                              v.id,
                                            )}
                                          >
                                            <FontAwesomeIcon icon={['fas', 'tools']} />
                                          </button>
                                        </Tips>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="no_trans">
                                    <div className="display_member">
                                      <img
                                        alt="profile"
                                        src={v.profile_image || `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                        className="profile_icon"
                                      />
                                      <ruby>
                                        {v.name}
                                        <rt>{v.kana}</rt>
                                      </ruby>
                                    </div>
                                  </td>
                                  <td className="no_trans">
                                    <div className="flow_wrapper">
                                      {buildApprovalFlowForPersonal(v.id)}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </>
                    )
                  }
                </div>
              </div>
            </section>
          </>
        )
      }
      {/* 承認グループ編集モーダル */}
      <ApproverSettingModal
        modalState={approverSettingModalState}
        closeCallback={closeApprovalModal}
        createCallback={(param, duplicationIds) => createApprovalGroup(param, duplicationIds)}
        updateCallback={(param, duplicationIds) => updateApprovalGroup(param, duplicationIds)}
      />
      {/* 承認グループ編集モーダル */}
      <ApproverSettingForMemberModal
        modalState={approverSettingForMemberModalState}
        closeCallback={closeApprovalForMemberModal}
        updateCallback={(approvalGroupId, memberId) => updateApprovalGroupForMember(approvalGroupId, memberId)}
      />
      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
    </section>
  )
}

export default Index
